import { create } from 'zustand';
import type { PlatformSettings, PlatformSettingsPatch } from '@shared/types';
import { api, errorMessage } from '../../lib/api';

/**
 * 平台设置。
 *
 * 看板要用分类标签、详情页要用 AI 开关，所以由 store 缓存一份，
 * 避免每进一个页面都重新拉一次。
 */

const FALLBACK: PlatformSettings = {
  aiEnabled: false,
  aiConfigured: false,
  aiDailyLimit: 0,
  aiModel: '',
  tags: [],
  capabilities: '',
};

interface SettingsState {
  settings: PlatformSettings;
  loading: boolean;
  loaded: boolean;
  error: string | null;
  load: (options?: { force?: boolean }) => Promise<void>;
  save: (patch: PlatformSettingsPatch) => Promise<PlatformSettings>;
  reset: () => void;
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  settings: FALLBACK,
  loading: false,
  loaded: false,
  error: null,

  async load(options = {}) {
    if (get().loading) return;
    if (get().loaded && !options.force) return;

    set({ loading: true, error: null });
    try {
      const settings = await api.get<PlatformSettings>('/api/settings');
      set({ settings, loading: false, loaded: true, error: null });
    } catch (error) {
      set({ loading: false, error: errorMessage(error) });
    }
  },

  async save(patch) {
    const settings = await api.patch<PlatformSettings>('/api/settings', patch);
    set({ settings, loaded: true, error: null });
    return settings;
  },

  reset() {
    set({ settings: FALLBACK, loading: false, loaded: false, error: null });
  },
}));
